import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import SearchTicker from "../../Views/Overview/SearchTicker";
import "./Navbar.scss";
// import NewsletterDialog from "./NewsletterSection/NewsletterDialog";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Search, X } from "lucide-react";
import { CircleUser } from "lucide-react";

const Navbar = () => {
  // Get current location
  let location = useLocation();

  const [mobileToggle, setMobileToggle] = useState<boolean>(false);
  // const dialogRef = useRef<HTMLDialogElement>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isNavigating, setIsNavigating] = useState(false);
  const [navigatingTicker, setNavigatingTicker] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);

  const { userInfo, setUserInfo } = useContext(AuthContext);

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchValue, isSearchOpen]);

  useEffect(() => {
    if (isSearchOpen && activeIndex >= 0 && resultsRef.current) {
      const activeEl = resultsRef.current.children[activeIndex] as HTMLElement;
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex, isSearchOpen]);

  const handleSearchSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const query = searchValue.trim().toLowerCase();
    if (!query) return;

    let targetTicker = "";
    if (activeIndex >= 0 && activeIndex < filteredResults.length) {
      targetTicker = filteredResults[activeIndex].value;
    } else if (filteredResults.length > 0) {
      targetTicker = filteredResults[0].value;
    } else {
      const exactMatch = optionsData.find(
        (s: any) => String(s?.label ?? "").toLowerCase() === query
      );
      if (exactMatch) targetTicker = exactMatch.value;
    }

    if (targetTicker) {
      onPickResult(targetTicker);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((prev) =>
          filteredResults.length > 0 ? (prev + 1) % filteredResults.length : -1
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((prev) =>
          filteredResults.length > 0
            ? (prev - 1 + filteredResults.length) % filteredResults.length
            : -1
        );
        break;
      case "Enter":
        e.preventDefault();
        handleSearchSubmit();
        break;
      default:
        break;
    }
  };

  const userLinks =
    userInfo == null
      ? [
          { name: "Login", href: "/login" },
          { name: "Register", href: "/register" },
        ]
      : [{ name: "Logout", href: "/" }];

  const links = [
    { name: "Home", href: "/", activeFor: [] as string[] },
    { name: "What's Trending?", href: "/trending", activeFor: [] as string[] },
    { name: "Investment Ideas", href: "/nifty-strategy", activeFor: ["/momentum-strategy", "/value-statergy"] },
    { name: "Dashboard", href: "/dashboard", activeFor: ["/detail", "/News", "/Tweets", "/TechnicalAnalysis"] as string[] },
    { name: "Semantic Search", href: "/search", activeFor: [] as string[] },
    { name: "News Aggregator", href: "/aggregator", activeFor: [] as string[] },
  ];

  const allLinks = [...userLinks, ...links];

  // console.log(new RegExp("/strong-stocks$").test(location.pathname));

  // const logout = () => {
  //   setUserInfo(null);
  // };

  const logout = async () => {
    try {
      // Call logout backend functionality to remove cookies from storage
      await fetch(
        `${process.env.REACT_APP_AUTH_URL || "http://localhost:3000"}/logout`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );
      setUserInfo(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
    //redirect page to home page when user logout
    window.location.href = "/";
  };

  //search ticker
  const [optionsData, setOptionsData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: summaryData } = await axios.get(
          `${process.env.REACT_APP_API_URL}/summary.json`,
        );

        if (summaryData?.summary) {
          const arr = summaryData.summary.map((item: any) => ({
            value: item.Stock_Ticker,
            label: item.Stock_Ticker,
          }));
          //sort by label
          arr.sort((a: any, b: any) => a.label.localeCompare(b.label));
          setOptionsData(arr);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const filteredResults = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return [];
    return optionsData
      .filter((s: any) => String(s?.label ?? "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [optionsData, searchValue]);

  useEffect(() => {
    if (!isSearchOpen) return;
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsSearchOpen(false);
        setSearchValue("");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isSearchOpen]);

  // Prevent background page scroll while modal is open.
  useEffect(() => {
    if (!isSearchOpen) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    // Hide scrollbars to avoid layout shift.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [isSearchOpen]);

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchValue("");
  };

  const onPickResult = (ticker: string) => {
    if (!ticker) return;
    setIsSearchOpen(false);
    setSearchValue("");
    setIsNavigating(true);
    setNavigatingTicker(ticker);
    window.localStorage.setItem("selectedTab", "0");
    window.localStorage.setItem("tickerId", ticker);
    setTimeout(() => {
      window.location.href = `/detail`;
    }, 3500);
  };

  return (
    <>
      {isNavigating && (
        <div className="nav-navigate-overlay">
          <div className="loadingspinner">
            <div id="square1" />
            <div id="square2" />
            <div id="square3" />
            <div id="square4" />
            <div id="square5" />
          </div>
          <p className="nav-navigate-label">Loading {navigatingTicker}…</p>
        </div>
      )}
      <nav className="landingv2-nav">
      <div className="nav-flex">
        {/* Main logo */}
        <div className="main-logo">
          <Link to="/" className="logo-link">
            <img src="/assets/images/landingv2/main-logo.png" alt="Main Logo" />
            {/* <div className="logo-text">
              <span className="brand-name">AlgoFabric</span>
              <span className="powered-by">POWERED BY ALGOANALYTICS</span>
            </div> */}
          </Link>
        </div>
        {/* Links */}
        <ul className={`links ${!mobileToggle && "links-hide-mobile"}`}>
          {links.map((link, index) => {
            const isActive =
              new RegExp(link.href + "$").test(location.pathname) ||
              link.activeFor.some((path) => location.pathname.startsWith(path));
            return (
              <li key={index} className={isActive ? "active" : ""}>
                <Link to={link.href}>{link.name}</Link>
              </li>
            );
          })}
        </ul>
        <div className="menu-header-search">
          {/* <SearchTicker stocks={optionsData} /> */}
        </div>
        <div className="header-NotificationIcon">
          <NotificationsNoneIcon className="notification-icon" style={{ display: 'none' }}/>
        </div>
        {/* Buttons */}
        <div className="buttons">
          {userInfo == null ? (
            <>
              <button
                type="button"
                className={`nav-icon-btn ${isSearchOpen ? "active" : ""}`}
                aria-label="Search"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search size={18} strokeWidth={2} />
              </button>
              {location.pathname !== "/login" && location.pathname !== "/register" && (
                <a href="/login">
                  <button className="register-btn">Register/Login</button>
                </a>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className={`nav-icon-btn ${isSearchOpen ? "active" : ""}`}
                aria-label="Search"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search size={18} strokeWidth={2} />
              </button>
              <button className="register-btn" onClick={logout}>Logout</button>
            </>
          )}
          {userInfo?.email === "test1@dev.com" && (
            <div id="monitoring-dashboard">
              <a href="https://algofabricmonitoringdashboard.streamlit.app/" target="_blank" rel="noreferrer">
                <Button>Monitoring Dashboard</Button>
              </a>
            </div>
          )}
        </div>
        {/* Mobile Hamburger */}
        <button
          className="mobile-button"
          onClick={() => setMobileToggle((prev) => !prev)}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.6667 4.16675C17.1269 4.16675 17.5 4.53984 17.5 5.00008C17.5 5.46032 17.1269 5.83341 16.6667 5.83341H3.33333C2.8731 5.83341 2.5 5.46032 2.5 5.00008C2.5 4.53984 2.8731 4.16675 3.33333 4.16675H16.6667ZM16.6667 9.16675C17.1269 9.16675 17.5 9.53984 17.5 10.0001C17.5 10.4603 17.1269 10.8334 16.6667 10.8334H3.33333C2.8731 10.8334 2.5 10.4603 2.5 10.0001C2.5 9.53984 2.8731 9.16675 3.33333 9.16675H16.6667ZM16.6667 14.1667C17.1269 14.1667 17.5 14.5398 17.5 15.0001C17.5 15.4603 17.1269 15.8334 16.6667 15.8334H3.33333C2.8731 15.8334 2.5 15.4603 2.5 15.0001C2.5 14.5398 2.8731 14.1667 3.33333 14.1667H16.6667Z"
              fill="#2D4890"
            />
          </svg>
        </button>
      </div>
      {/* <NewsletterDialog dialogRef={dialogRef} /> */}
    </nav>

    {isSearchOpen && (
      <div
        className="nav-search-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) closeSearch();
        }}
      >
        <div className="nav-search-modal">
          <div className="nav-search-top">
            <div className="nav-search-title">Search</div>
            <button type="button" className="nav-search-close" aria-label="Close" onClick={closeSearch}>
              <X size={18} />
            </button>
          </div>

          <form onSubmit={handleSearchSubmit}>
            <div className="nav-search-input-wrap">
              <button type="submit" className="nav-search-submit-btn" aria-label="Submit search">
                <Search size={16} className="nav-search-input-icon" />
              </button>
              <input
                ref={searchInputRef}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="nav-search-input"
                placeholder="Type here to search..."
                autoComplete="off"
              />
              {searchValue.trim().length > 0 && (
                <button
                  type="button"
                  className="nav-search-clear"
                  aria-label="Clear"
                  onClick={() => setSearchValue("")}
                >
                  Clear
                </button>
              )}
            </div>
          </form>

          {searchValue.trim().length > 0 && (
            <div className="nav-search-results" ref={resultsRef}>
              {filteredResults.length > 0 ? (
                filteredResults.map((r: any, index) => (
                  <button
                    key={r.value}
                    type="button"
                    className={`nav-search-result ${index === activeIndex ? "active" : ""}`}
                    onClick={() => onPickResult(r.value)}
                  >
                    <div className="nav-search-result-main">
                      <div className="nav-search-result-title">{r.label}</div>
                      {/* <div className="nav-search-result-subtitle">Stock</div> */}
                    </div>
                  </button>
                ))
              ) : (
                <div className="nav-search-empty">No results</div>
              )}
            </div>
          )}
        </div>
      </div>
    )}    </>
  );
};

export default Navbar;
